import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { Field, Modal, useToast } from "@cx/ui";
import type {
  LogisticsIntegrationView,
  LogisticsSettings,
  PackageInput,
  ShipmentFormOptions,
  ShipmentView,
  ShippingQuote,
  ShippingQuoteOption,
} from "@cx/contracts";
import {
  CORREIOS_DEFAULT_SERVICES,
  PACKAGE_MAX_SIDE_CM,
  REVERSE_DAYS_DEFAULT,
  REVERSE_DAYS_MAX,
  REVERSE_DAYS_MIN,
  formatBRL,
} from "@cx/domain";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { useOpenKey } from "../invoices/InvoiceEvents";
import { cepLabel, daysLabel, gramsToKgInput, kgToGrams, providerLabel } from "./labels";

/**
 * Diálogos da logística (T3.4, RF-VEN-10): integração (Correios e configuração), gerar etiqueta a partir do pedido
 * (cotação → objeto), cancelar e logística reversa. Uma Idempotency-Key por abertura (renovada se não for 409).
 */

// ---------- pacote ----------

export type PackageText = { weight: string; height: string; width: string; length: string };
export const packageText = (p: PackageInput | null): PackageText =>
  p
    ? {
        weight: gramsToKgInput(p.weightG),
        height: String(p.heightCm),
        width: String(p.widthCm),
        length: String(p.lengthCm),
      }
    : { weight: "", height: "", width: "", length: "" };

const cm = (s: string) => (/^\d{1,3}$/.test(s.trim()) ? Number(s.trim()) : null);
/** Texto do formulário → pacote em inteiros (null = algum campo inválido). */
export function packageOf(t: PackageText): PackageInput | null {
  const weightG = kgToGrams(t.weight);
  const heightCm = cm(t.height);
  const widthCm = cm(t.width);
  const lengthCm = cm(t.length);
  if (weightG === null || !heightCm || !widthCm || !lengthCm) return null;
  if ([heightCm, widthCm, lengthCm].some((v) => v > PACKAGE_MAX_SIDE_CM)) return null;
  return { weightG, heightCm, widthCm, lengthCm };
}
const emptyPackage = (t: PackageText) => !t.weight && !t.height && !t.width && !t.length;

function PackageFields({
  value,
  onChange,
  error,
}: {
  value: PackageText;
  onChange(v: PackageText): void;
  error?: string;
}): ReactElement {
  const set = (k: keyof PackageText) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <div className="row">
      <Field label="Peso (kg)" className="col-6 col-md-3" error={error}>
        <input className="form-control" inputMode="decimal" value={value.weight} onChange={set("weight")} />
      </Field>
      <Field label="Comprimento (cm)" className="col-6 col-md-3">
        <input className="form-control" inputMode="numeric" value={value.length} onChange={set("length")} />
      </Field>
      <Field label="Largura (cm)" className="col-6 col-md-3">
        <input className="form-control" inputMode="numeric" value={value.width} onChange={set("width")} />
      </Field>
      <Field label="Altura (cm)" className="col-6 col-md-3">
        <input className="form-control" inputMode="numeric" value={value.height} onChange={set("height")} />
      </Field>
    </div>
  );
}

// ---------- integração ----------

type IntegrationDialogProps = {
  open: boolean;
  /** null = conectar os Correios. */
  integration: LogisticsIntegrationView | null;
  onClose(): void;
  onDone(i: LogisticsIntegrationView): void;
};

const emptyCredentials = { user: "", accessCode: "", postingCard: "", contract: "", dr: "" };

/** Conectar os Correios (credenciais do CWS conferidas na hora) ou editar nome e configuração de uma integração. */
export function IntegrationDialog({
  open,
  integration,
  onClose,
  onDone,
}: IntegrationDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const creating = integration === null;
  const correios = creating || integration.provider === "correios";
  const [name, setName] = useState("");
  const [services, setServices] = useState<{ code: string; name: string }[]>([]);
  const [pkg, setPkg] = useState<PackageText>(packageText(null));
  const [declareValue, setDeclareValue] = useState(true);
  const [reverseDays, setReverseDays] = useState(String(REVERSE_DAYS_DEFAULT));
  const [changeCred, setChangeCred] = useState(false);
  const [cred, setCred] = useState(emptyCredentials);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!open) return;
    const s = integration?.settings;
    setName(integration?.name ?? "Correios");
    setServices(s ? s.services : [...CORREIOS_DEFAULT_SERVICES]);
    setPkg(packageText(s?.defaultPackage ?? null));
    setDeclareValue(s?.declareValue ?? true);
    setReverseDays(String(s?.reverseDays ?? REVERSE_DAYS_DEFAULT));
    setChangeCred(creating);
    setCred(emptyCredentials);
    setTouched(false);
  }, [open, integration, creating]);
  const pkgValue = emptyPackage(pkg) ? null : packageOf(pkg);
  const days = /^\d{1,2}$/.test(reverseDays) ? Number(reverseDays) : NaN;
  const problems = {
    name: name.trim() ? undefined : "Informe o nome",
    pkg: !emptyPackage(pkg) && !pkgValue ? "Pacote inválido: peso até 3 casas e medidas inteiras" : undefined,
    days:
      days >= REVERSE_DAYS_MIN && days <= REVERSE_DAYS_MAX
        ? undefined
        : `De ${REVERSE_DAYS_MIN} a ${REVERSE_DAYS_MAX} dias`,
    services:
      correios && services.some((s) => !/^\d{5}$/.test(s.code.trim()) || !s.name.trim())
        ? "Cada serviço precisa de código (5 dígitos) e nome"
        : undefined,
    cred:
      changeCred && (!cred.user.trim() || !cred.accessCode.trim() || !cred.postingCard || !cred.contract)
        ? "Informe usuário, código de acesso, cartão de postagem e contrato"
        : undefined,
  };
  const invalid = Object.values(problems).some(Boolean);
  const settings = (): LogisticsSettings => ({
    services: correios ? services.map((s) => ({ code: s.code.trim(), name: s.name.trim() })) : [],
    defaultPackage: pkgValue,
    declareValue,
    reverseDays: days,
  });
  const credentials = () => ({
    user: cred.user.trim(),
    accessCode: cred.accessCode.trim(),
    postingCard: cred.postingCard.trim(),
    contract: cred.contract.trim(),
    dr: cred.dr ? Number(cred.dr) : null,
  });
  const save = useMutation({
    mutationFn: () =>
      creating
        ? api<LogisticsIntegrationView>("/logistics/integrations/correios", {
            method: "POST",
            body: JSON.stringify({ name: name.trim(), credentials: credentials(), settings: settings() }),
          })
        : api<LogisticsIntegrationView>(`/logistics/integrations/${integration.id}`, {
            method: "PUT",
            body: JSON.stringify({
              name: name.trim(),
              settings: settings(),
              ...(changeCred ? { credentials: credentials() } : {}),
            }),
          }),
    onSuccess: (i) => {
      toast("success", creating ? "Correios conectados" : "Integração salva");
      onDone(i);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const credField = (k: keyof typeof cred) => ({
    value: cred[k],
    onChange: (e: { target: { value: string } }) => setCred({ ...cred, [k]: e.target.value }),
  });
  return (
    <Modal
      open={open}
      title={creating ? "Conectar Correios" : `Configurar ${integration.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => (invalid ? setTouched(true) : save.mutate())}
          >
            {creating ? "Conectar" : "Salvar"}
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={touched ? problems.name : undefined}>
          <input
            className="form-control"
            data-autofocus
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      </div>
      {correios && !creating && (
        <div className="form-check mb-3">
          <input
            id="lg-change-cred"
            type="checkbox"
            className="form-check-input"
            checked={changeCred}
            onChange={(e) => setChangeCred(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="lg-change-cred">
            Trocar as credenciais do contrato
          </label>
        </div>
      )}
      {correios && changeCred && (
        <fieldset className="mb-2">
          <legend className="h6">Contrato com os Correios</legend>
          <p className="small text-muted">
            Usuário e código de acesso à API gerados no portal Meu Correios (CWS). Ficam cifrados e nunca são
            exibidos de novo.
          </p>
          <div className="row">
            <Field label="Usuário do Meu Correios" required>
              <input className="form-control" autoComplete="off" {...credField("user")} />
            </Field>
            <Field label="Código de acesso à API" required>
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                {...credField("accessCode")}
              />
            </Field>
            <Field label="Cartão de postagem" required className="col-md-5">
              <input className="form-control" inputMode="numeric" {...credField("postingCard")} />
            </Field>
            <Field label="Contrato" required className="col-md-5">
              <input className="form-control" inputMode="numeric" {...credField("contract")} />
            </Field>
            <Field label="DR" className="col-md-2">
              <input className="form-control" inputMode="numeric" maxLength={2} {...credField("dr")} />
            </Field>
          </div>
          {touched && problems.cred && <div className="text-danger small mb-2">{problems.cred}</div>}
        </fieldset>
      )}
      {correios && (
        <fieldset className="mb-3">
          <legend className="h6">Serviços cotados</legend>
          {services.map((s, i) => (
            <div className="d-flex gap-2 mb-2" key={i}>
              <input
                className="form-control"
                style={{ maxWidth: 110 }}
                aria-label={`Código do serviço ${i + 1}`}
                inputMode="numeric"
                maxLength={5}
                value={s.code}
                onChange={(e) =>
                  setServices(services.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))
                }
              />
              <input
                className="form-control"
                aria-label={`Nome do serviço ${i + 1}`}
                maxLength={40}
                value={s.name}
                onChange={(e) =>
                  setServices(services.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                }
              />
              <button
                type="button"
                className="btn btn-outline-danger"
                aria-label={`Remover serviço ${i + 1}`}
                onClick={() => setServices(services.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          {services.length < 10 && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setServices([...services, { code: "", name: "" }])}
            >
              Adicionar serviço
            </button>
          )}
          {touched && problems.services && <div className="text-danger small mt-2">{problems.services}</div>}
        </fieldset>
      )}
      <fieldset>
        <legend className="h6">Pacote padrão</legend>
        <p className="small text-muted">Opcional. Sem ele, o pacote é sugerido pelos produtos do pedido.</p>
        <PackageFields value={pkg} onChange={setPkg} error={touched ? problems.pkg : undefined} />
      </fieldset>
      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              id="lg-declare"
              type="checkbox"
              className="form-check-input"
              checked={declareValue}
              onChange={(e) => setDeclareValue(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="lg-declare">
              Declarar o valor dos produtos (seguro)
            </label>
          </div>
        </div>
        <Field
          label="Validade da logística reversa (dias)"
          className="col-md-6"
          error={touched ? problems.days : undefined}
        >
          <input
            className="form-control"
            inputMode="numeric"
            value={reverseDays}
            onChange={(e) => setReverseDays(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------- gerar etiqueta (pedido) ----------

const optionLabel = (o: ShippingQuoteOption) =>
  `${o.carrierName ? `${o.carrierName} ` : ""}${o.serviceName}`.trim();

/** Gerar etiqueta a partir do pedido: integração → pacote → cotação → escolher o serviço → objeto (etiqueta pela fila). */
export function ShipmentDialog({
  open,
  salesOrderId,
  onClose,
  onDone,
}: {
  open: boolean;
  salesOrderId: string;
  onClose(): void;
  onDone(s: ShipmentView): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const toast = useToast();
  const { key, renew } = useOpenKey(open);
  const opts = useQuery({
    queryKey: ["logistics", companyId, "form-options", salesOrderId],
    queryFn: () =>
      api<ShipmentFormOptions>(
        `/logistics/shipments/form-options?salesOrderId=${encodeURIComponent(salesOrderId)}`,
      ),
    enabled: open,
  });
  const [integrationId, setIntegrationId] = useState("");
  const [pkg, setPkg] = useState<PackageText>(packageText(null));
  const [quote, setQuote] = useState<ShippingQuote | null>(null);
  const [service, setService] = useState("");
  const [notes, setNotes] = useState("");
  const o = opts.data;
  useEffect(() => {
    if (!open || !o) return;
    const first = o.integrations[0];
    setIntegrationId(first?.id ?? "");
    setPkg(packageText(first?.defaultPackage ?? o.suggestedPackage));
    setQuote(null);
    setService("");
    setNotes("");
  }, [open, o]);
  const pkgValue = packageOf(pkg);
  const doQuote = useMutation({
    mutationFn: () =>
      api<ShippingQuote>("/logistics/shipments/quote", {
        method: "POST",
        body: JSON.stringify({ integrationId, salesOrderId, package: pkgValue }),
      }),
    onSuccess: (q) => {
      setQuote(q);
      setService(q.options.find((x) => !x.error && x.priceCents !== null)?.serviceCode ?? "");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const chosen = quote?.options.find((x) => x.serviceCode === service && !x.error);
  const create = useMutation({
    mutationFn: () =>
      api<ShipmentView>("/logistics/shipments", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          salesOrderId,
          integrationId,
          serviceCode: chosen!.serviceCode,
          serviceName: chosen!.serviceName,
          carrierName: chosen!.carrierName,
          quotedCents: chosen!.priceCents,
          deliveryDays: chosen!.deliveryDays,
          package: pkgValue,
          declaredValueCents: null,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: (s) => {
      toast("success", `Objeto ${s.number} criado. A etiqueta está sendo gerada.`);
      onDone(s);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  const stale = (fn: () => void) => () => {
    fn();
    setQuote(null);
    setService("");
  };
  const blocked = !o || o.integrations.length === 0 || o.issues.length > 0 || !!o.activeShipment;
  return (
    <Modal
      open={open}
      title="Gerar etiqueta"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={blocked || !integrationId || !pkgValue || doQuote.isPending}
            onClick={() => doQuote.mutate()}
          >
            Cotar frete
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked || !chosen || !pkgValue || create.isPending}
            onClick={() => create.mutate()}
          >
            Gerar etiqueta
          </button>
        </>
      }
    >
      {opts.isLoading && <p className="mb-0">Carregando…</p>}
      {opts.isError && <div className="alert alert-danger py-2">{errorMessage(opts.error)}</div>}
      {o && (
        <>
          <p className="small mb-2">
            Pedido <strong>{o.salesOrder.number}</strong> · {o.salesOrder.contactName} · produtos{" "}
            {formatBRL(o.salesOrder.itemsTotalCents)}
          </p>
          {o.recipient && (
            <p className="small text-muted">
              Entrega: {o.recipient.street}, {o.recipient.number}
              {o.recipient.complement ? ` ${o.recipient.complement}` : ""} — {o.recipient.district},{" "}
              {o.recipient.city}/{o.recipient.uf} · CEP {cepLabel(o.recipient.cep)}
            </p>
          )}
          {o.invoice ? (
            <p className="small text-muted">NF-e {o.invoice.number} vai junto na declaração do objeto.</p>
          ) : (
            <p className="small text-muted">Sem NF-e autorizada: o objeto sai com declaração de conteúdo.</p>
          )}
          {o.activeShipment && (
            <div className="alert alert-warning py-2">
              O pedido já tem o objeto {o.activeShipment.number}. Cancele-o para gerar outra etiqueta.
            </div>
          )}
          {o.issues.length > 0 && (
            <div className="alert alert-warning py-2" role="alert">
              <ul className="mb-0 ps-3">
                {o.issues.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          )}
          {o.integrations.length === 0 ? (
            <div className="alert alert-info py-2">
              Nenhuma integração de logística ativa. Conecte os Correios ou o Melhor Envio em Preferências ›
              Integrações › Logística.
            </div>
          ) : (
            <>
              <div className="row">
                <Field label="Integração" className="col-12">
                  <select
                    className="form-select"
                    value={integrationId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const def = o.integrations.find((i) => i.id === id)?.defaultPackage;
                      stale(() => {
                        setIntegrationId(id);
                        if (def) setPkg(packageText(def));
                      })();
                    }}
                  >
                    {o.integrations.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({providerLabel(i.provider)})
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <PackageFields
                value={pkg}
                onChange={(v) => stale(() => setPkg(v))()}
                error={pkgValue ? undefined : "Pacote inválido: peso até 3 casas e medidas inteiras"}
              />
              {quote && (
                <fieldset className="mb-3">
                  <legend className="h6">Serviço</legend>
                  {quote.options.length === 0 && (
                    <p className="small text-muted">Nenhum serviço disponível.</p>
                  )}
                  {quote.options.map((x) => (
                    <div className="form-check" key={x.serviceCode}>
                      <input
                        id={`lg-svc-${x.serviceCode}`}
                        type="radio"
                        name="lg-service"
                        className="form-check-input"
                        disabled={!!x.error || x.priceCents === null}
                        checked={service === x.serviceCode}
                        onChange={() => setService(x.serviceCode)}
                      />
                      <label className="form-check-label" htmlFor={`lg-svc-${x.serviceCode}`}>
                        {optionLabel(x)} —{" "}
                        {x.error ? (
                          <span className="text-muted">{x.error}</span>
                        ) : (
                          <>
                            {x.priceCents === null ? "—" : formatBRL(x.priceCents)} ·{" "}
                            {daysLabel(x.deliveryDays)}
                          </>
                        )}
                      </label>
                    </div>
                  ))}
                </fieldset>
              )}
              <div className="row">
                <Field label="Observações" className="col-12">
                  <input
                    className="form-control"
                    maxLength={500}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Field>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}

// ---------- cancelar ----------

export function CancelShipmentDialog({
  open,
  shipment,
  onClose,
  onDone,
}: {
  open: boolean;
  shipment: ShipmentView;
  onClose(): void;
  onDone(s: ShipmentView): void;
}): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const { key, renew } = useOpenKey(open);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);
  const len = reason.trim().length;
  const problem = len < 5 || len > 255 ? "Motivo: de 5 a 255 caracteres" : undefined;
  const send = useMutation({
    mutationFn: () =>
      api<ShipmentView>(`/logistics/shipments/${shipment.id}/cancel`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: (s) => {
      toast("success", s.status === "canceled" ? "Objeto cancelado" : "Cancelamento enviado ao provedor");
      onDone(s);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title={`Cancelar objeto ${shipment.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            Cancelar objeto
          </button>
        </>
      }
    >
      <p className="small">
        A etiqueta é cancelada no {providerLabel(shipment.provider)}
        {shipment.provider === "melhorenvio" ? " (o valor volta para a carteira)" : ""}. Objeto já postado não
        pode ser cancelado.
      </p>
      <div className="row">
        <Field label="Motivo" required className="col-12" error={touched ? problem : undefined}>
          <textarea
            className="form-control"
            data-autofocus
            rows={2}
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------- logística reversa ----------

export function ReverseDialog({
  open,
  shipment,
  onClose,
  onDone,
}: {
  open: boolean;
  shipment: ShipmentView;
  onClose(): void;
  onDone(s: ShipmentView): void;
}): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const { key, renew } = useOpenKey(open);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setNotify(!!shipment.recipient.email);
      setTouched(false);
    }
  }, [open, shipment.recipient.email]);
  const len = reason.trim().length;
  const problem = len < 3 || len > 255 ? "Motivo: de 3 a 255 caracteres" : undefined;
  const send = useMutation({
    mutationFn: () =>
      api<ShipmentView>(`/logistics/shipments/${shipment.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          serviceCode: shipment.serviceCode,
          serviceName: shipment.serviceName,
          carrierName: shipment.carrierName,
          package: null,
          notifyCustomer: notify,
          reason: reason.trim(),
        }),
      }),
    onSuccess: (s) => {
      toast("success", `Logística reversa ${s.number} criada. A autorização está sendo emitida.`);
      onDone(s);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title={`Logística reversa do objeto ${shipment.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            Gerar autorização
          </button>
        </>
      }
    >
      <p className="small">
        O cliente {shipment.recipient.name} devolve o pacote pelo mesmo serviço ({shipment.serviceName}), com
        o mesmo pacote do envio. A autorização de postagem vale pelo prazo configurado na integração.
      </p>
      <div className="row">
        <Field label="Motivo da devolução" required className="col-12" error={touched ? problem : undefined}>
          <input
            className="form-control"
            data-autofocus
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
      <div className="form-check">
        <input
          id="lg-notify"
          type="checkbox"
          className="form-check-input"
          disabled={!shipment.recipient.email}
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="lg-notify">
          Enviar a autorização por e-mail ao cliente
          {shipment.recipient.email ? ` (${shipment.recipient.email})` : " (cliente sem e-mail)"}
        </label>
      </div>
    </Modal>
  );
}
